<script lang="ts" module>
	// The paper strip's measures, in millimetres, kept here and nowhere else
	// (design/components/surfaces/ReceiptPreview.jsx): the printed width inside
	// the roll and its side margins, the blank paper above the first line, and
	// the feed and cut below the last. The browser draws these margins for the
	// feel of paper, not pixel-exact (decision 2026-10-18-paper-true-print-preview).
	export const paper = {
		80: { printed: 72, side: 4 },
		58: { printed: 48, side: 5 },
	} as const;
	export const paperTopMM = 4;
	export const paperFeedMM = 14;
</script>

<script lang="ts">
	// The design system's ReceiptPreview (design/components/surfaces/
	// ReceiptPreview.jsx). With paperMM it is the paper strip of Settings →
	// Printing (#637): the roll at its real width in CSS millimetres, 80 or 58,
	// holding the printer's own image (the caller's child) at the printed width,
	// 72 or 48 mm, scaled smoothly rather than stretched to the card, with a
	// dashed edge where the paper is cut. The paper stays white in both themes,
	// because the image it holds is white paper.
	//
	// Without paperMM it is the plain receipt card: the header and footer sit
	// above and below dashed rules, the lines between are the caller's.
	//
	// It draws; it does not print. The printed receipt comes from the server.

	import type { Snippet } from 'svelte';

	interface Props {
		header?: string;
		footer?: string;
		/** Draws the paper strip at this roll width. */
		paperMM?: 58 | 80;
		testId?: string;
		children?: Snippet;
	}

	let { header, footer, paperMM, testId, children }: Props = $props();

	const roll = $derived(paperMM ? paper[paperMM] : undefined);
</script>

{#if paperMM && roll}
	<div class="desk" data-testid={testId}>
		<div
			class="paper"
			data-paper={paperMM}
			data-testid={testId ? testId + '-paper' : undefined}
			style:--roll="{paperMM}mm"
			style:--side="{roll.side}mm"
			style:--printed="{roll.printed}mm"
			style:--top="{paperTopMM}mm"
			style:--feed="{paperFeedMM}mm"
		>
			<div class="printed">{@render children?.()}</div>
			<div class="cut" data-testid={testId ? testId + '-cut' : undefined}></div>
		</div>
	</div>
{:else}
	<div class="receipt" data-testid={testId}>
		{#if header}
			<p class="header">{header}</p>
		{/if}
		<div class:ruled={Boolean(header)}>
			{@render children?.()}
		</div>
		{#if footer}
			<p class="footer">{footer}</p>
		{/if}
	</div>
{/if}

<style>
	/* The paper keeps its real size; a column narrower than the roll scrolls
	 * it sideways rather than letting it run over the controls beside it. The
	 * auto margins centre it when there is room and never clip its start. */
	.desk {
		display: flex;
		min-inline-size: 0;
		overflow-x: auto;
		padding: var(--space-5);
		background: var(--surface-well);
		border-radius: var(--radius-input);
	}

	.paper {
		box-sizing: border-box;
		inline-size: var(--roll);
		flex: none;
		margin-inline: auto;
		padding-inline: var(--side);
		padding-block-start: var(--top);
		/* Thermal paper, white in both themes, as the image it holds. */
		background: var(--receipt-paper);
		border: var(--border-width) solid var(--border-default);
	}

	.printed {
		inline-size: var(--printed);
	}

	.cut {
		block-size: var(--feed);
		border-block-end: 1px dashed var(--border-default);
	}

	.receipt {
		background: var(--surface-card-header);
		border: 1px dashed var(--border-default);
		border-radius: 4px;
		padding: 18px 16px;
		font-family: var(--font-mono);
		font-size: 12px;
		line-height: 2;
		color: var(--ink);
	}

	.header {
		margin: 0;
		text-align: center;
		font-weight: 700;
		font-size: 13px;
	}

	.ruled,
	.footer {
		border-block-start: 1px dashed var(--border-default);
		margin-block-start: 10px;
		padding-block-start: 10px;
	}

	.footer {
		margin-block-end: 0;
		margin-inline: 0;
		text-align: center;
		font-size: 11px;
		color: var(--ink-body);
	}
</style>
